/**
 * Merged-outline ring extraction and construction.
 *
 * The floor's outer wall ("outline") lives INSIDE the wall-sketch graph as
 * edges tagged `role: 'OUTLINE'` (zero-thickness boundary lines, never
 * skinned). This module turns that subgraph into the closed polygon rings
 * every measured-mode consumer needs (footprint, 3D floor mesh, exports,
 * generation), and back.
 *
 * Tolerance model: only CLOSED cycles count — a cycle is closed when every
 * node on it has exactly two incident outline edges. Open chains render and
 * stay editable but contribute no rings, exactly like "no outline yet". Hole
 * classification is geometric (containment nesting depth: even = outer ring,
 * odd = hole of its innermost container); windings are normalized to the
 * polygon-clipping convention (outer CCW, holes CW).
 *
 * Legacy compatibility: `resolveFloorOutlineRings` / `resolveOutlineRingsFromAnnotations`
 * read merged rings first and fall back to the floor's legacy OUTLINE
 * annotation(s), so unconverted projects keep working with zero writes.
 */

import { v4 as uuid } from 'uuid';
import { getOutlineCavitiesProperty, getOutlineOuterRingProperty, getWallSketchEdgesProperty, getWallSketchNodesProperty, type SkeletonEdgeData, type SkeletonNodeData } from '@pointorama/potree-shared';
import { tessellateEdge } from './arcGeometry';
import type { Viewer } from '../../viewer/viewer';

/** One connected outline component: outer boundary + interior holes. */
export interface OutlineRingComponent {
  /** CCW, closed implicitly (last point does not repeat the first). */
  outerRing: { x: number; y: number; z: number }[];
  /** CW, closed implicitly. */
  holes: { x: number; y: number; z: number }[][];
}

/** Minimal annotation shape the data-side resolver needs. */
export interface OutlineSourceAnnotation {
  identifier: string;
  type: string;
  groupIdentifier?: string | null;
}

// ─── Extraction (graph → rings) ───────────────────────────────────────────

interface Cycle {
  points: { x: number; y: number; z: number }[];
  /** Shoelace signed area over XY (positive = CCW). */
  signedArea: number;
  /** Ids of the outline edges forming the cycle. */
  edgeIds: string[];
}

/**
 * Extract the closed `role:'OUTLINE'` cycles of a sketch graph as classified
 * ring components. Open chains and dangling edges are ignored. Curved edges
 * (bulge) are tessellated so consumers receive plain polygons.
 */
export function extractOutlineRings(nodes: readonly SkeletonNodeData[], edges: readonly SkeletonEdgeData[]): OutlineRingComponent[] {
  // A closed ring needs at least two edges (e.g. a straight chord plus a
  // bulged arc forming a half-moon).
  const outlineEdges = edges.filter((e) => e.role === 'OUTLINE');
  if (outlineEdges.length < 2) return [];

  const nodesById = new Map<string, SkeletonNodeData>();
  for (const n of nodes) nodesById.set(n.id, n);

  // Outline-subgraph adjacency. Cycles require degree EXACTLY 2 at every node.
  const incident = new Map<string, SkeletonEdgeData[]>();
  for (const e of outlineEdges) {
    if (!nodesById.has(e.startNodeId) || !nodesById.has(e.endNodeId)) continue;
    for (const nodeId of [e.startNodeId, e.endNodeId]) {
      const list = incident.get(nodeId);
      if (list) list.push(e);
      else incident.set(nodeId, [e]);
    }
  }

  const visited = new Set<string>();
  const cycles: Cycle[] = [];

  for (const startEdge of outlineEdges) {
    if (visited.has(startEdge.id)) continue;
    const cycle = walkCycle(startEdge, nodesById, incident, visited);
    if (cycle && cycle.points.length >= 3) cycles.push(cycle);
  }
  if (cycles.length === 0) return [];

  // Nesting-depth classification: even depth = outer ring, odd = hole of its
  // innermost even container. Depth via containment of a representative point.
  const byDepth = cycles.map((cycle) => {
    const probe = ringMidpointProbe(cycle.points);
    const depth = cycles.reduce((d, other) => (other !== cycle && pointInRingXY(probe.x, probe.y, other.points) ? d + 1 : d), 0);
    return { cycle, depth };
  });

  const components: { component: OutlineRingComponent; cycle: Cycle }[] = [];
  for (const { cycle, depth } of byDepth) {
    if (depth % 2 !== 0) continue;
    components.push({ component: { outerRing: withWinding(cycle, true), holes: [] }, cycle });
  }
  for (const { cycle, depth } of byDepth) {
    if (depth % 2 === 0) continue;
    // Innermost even-depth container = the smallest outer ring containing it.
    const probe = ringMidpointProbe(cycle.points);
    let container: { component: OutlineRingComponent; cycle: Cycle } | null = null;
    for (const candidate of components) {
      if (!pointInRingXY(probe.x, probe.y, candidate.cycle.points)) continue;
      if (!container || Math.abs(candidate.cycle.signedArea) < Math.abs(container.cycle.signedArea)) container = candidate;
    }
    container?.component.holes.push(withWinding(cycle, false));
  }
  return components.map((c) => c.component);
}

/** Walk the cycle containing `startEdge`; null when it runs into a node whose
 *  outline degree isn't 2 (open chain / branch) — those edges stay visited so
 *  each chain is inspected once. */
function walkCycle(startEdge: SkeletonEdgeData, nodesById: Map<string, SkeletonNodeData>, incident: Map<string, SkeletonEdgeData[]>, visited: Set<string>): Cycle | null {
  const points: { x: number; y: number; z: number }[] = [];
  const walked: { edge: SkeletonEdgeData; reversed: boolean }[] = [];

  const firstNodeId = startEdge.startNodeId;
  let currentEdge = startEdge;
  let currentNodeId = firstNodeId; // node we entered currentEdge from
  let closed = false;

  while (true) {
    visited.add(currentEdge.id);
    const reversed = currentEdge.endNodeId === currentNodeId;
    walked.push({ edge: currentEdge, reversed });
    const nextNodeId = reversed ? currentEdge.startNodeId : currentEdge.endNodeId;
    if (nextNodeId === firstNodeId) {
      closed = true;
      break;
    }
    const nextEdges = incident.get(nextNodeId) ?? [];
    if (nextEdges.length !== 2) break; // branch or dangling end — not a ring
    const next = nextEdges[0].id === currentEdge.id ? nextEdges[1] : nextEdges[0];
    if (visited.has(next.id)) break; // self-touching configuration — bail
    currentEdge = next;
    currentNodeId = nextNodeId;
  }
  // The closing node must also have degree 2 — a figure-eight pivot is not a ring.
  if (!closed || (incident.get(firstNodeId) ?? []).length !== 2) return null;

  for (const { edge, reversed } of walked) {
    const start = nodesById.get(reversed ? edge.endNodeId : edge.startNodeId);
    const end = nodesById.get(reversed ? edge.startNodeId : edge.endNodeId);
    if (!start || !end) return null;
    // Reversing an arc's direction flips the bulge sign (same circle, other way).
    const bulge = edge.bulge === undefined ? undefined : reversed ? -edge.bulge : edge.bulge;
    const pts = tessellateEdge({ x: start.position.x, y: start.position.y }, { x: end.position.x, y: end.position.y }, bulge);
    // Drop each edge's last point — it is the next edge's first.
    for (let i = 0; i < pts.length - 1; i++) points.push({ x: pts[i].x, y: pts[i].y, z: start.position.z });
  }

  return { points, signedArea: signedAreaXY(points), edgeIds: walked.map((w) => w.edge.id) };
}

/**
 * Ids of `role:'OUTLINE'` edges that are NOT part of any closed cycle — open
 * chains, branches, dangling segments. The error overlay tints these red as
 * the "ring not closed" signal (an unclosed outline contributes nothing to
 * the footprint/exports).
 */
export function collectOpenOutlineEdgeIds(nodes: readonly SkeletonNodeData[], edges: readonly SkeletonEdgeData[]): Set<string> {
  const outlineEdges = edges.filter((e) => e.role === 'OUTLINE');
  const open = new Set<string>(outlineEdges.map((e) => e.id));
  if (outlineEdges.length < 2) return open;

  const nodesById = new Map<string, SkeletonNodeData>();
  for (const n of nodes) nodesById.set(n.id, n);
  const incident = new Map<string, SkeletonEdgeData[]>();
  for (const e of outlineEdges) {
    if (!nodesById.has(e.startNodeId) || !nodesById.has(e.endNodeId)) continue;
    for (const nodeId of [e.startNodeId, e.endNodeId]) {
      const list = incident.get(nodeId);
      if (list) list.push(e);
      else incident.set(nodeId, [e]);
    }
  }

  const visited = new Set<string>();
  for (const startEdge of outlineEdges) {
    if (visited.has(startEdge.id)) continue;
    const cycle = walkCycle(startEdge, nodesById, incident, visited);
    if (!cycle || cycle.points.length < 3) continue;
    for (const id of cycle.edgeIds) open.delete(id);
  }
  return open;
}

// ─── Resolvers (merged first, legacy OUTLINE annotation fallback) ─────────

/** Scene-side resolver: the floor's merged rings, else its legacy OUTLINE
 *  annotation rings. Empty when the floor has neither. */
export function resolveFloorOutlineRings(viewer: Viewer, floorGroupId: string): OutlineRingComponent[] {
  const sketch = viewer.scene.wallSketches.find((s) => s.groupIdentifier === floorGroupId);
  if (sketch) {
    const merged = extractOutlineRings(sketch.getNodes(), sketch.getEdges());
    if (merged.length > 0) return merged;
  }
  const components: OutlineRingComponent[] = [];
  for (const outline of viewer.scene.outlines) {
    if (outline.groupIdentifier !== floorGroupId) continue;
    const outerRing = outline.getOuterRing().map((p) => ({ x: p.x, y: p.y, z: p.z }));
    if (outerRing.length < 3) continue;
    components.push({ outerRing, holes: outline.getHoles().map((h) => h.map((p) => ({ x: p.x, y: p.y, z: p.z }))) });
  }
  return components;
}

/** Data-side resolver for export hooks / gating: reads the floor's sketch
 *  properties, else its legacy OUTLINE annotation properties, from an
 *  annotation list (no scene objects required). */
export function resolveOutlineRingsFromAnnotations(annotations: readonly OutlineSourceAnnotation[], floorGroupId: string): OutlineRingComponent[] {
  try {
    const sketchAnnotation = annotations.find((a) => a.type === 'WALL_SKETCH' && a.groupIdentifier === floorGroupId);
    if (sketchAnnotation) {
      const merged = extractOutlineRings(getWallSketchNodesProperty(sketchAnnotation.identifier).value, getWallSketchEdgesProperty(sketchAnnotation.identifier).value);
      if (merged.length > 0) return merged;
    }
    const components: OutlineRingComponent[] = [];
    for (const a of annotations) {
      if (a.type !== 'OUTLINE' || a.groupIdentifier !== floorGroupId) continue;
      const outerRing = getOutlineOuterRingProperty(a.identifier).value.map((p) => ({ x: p.x, y: p.y, z: p.z }));
      if (outerRing.length < 3) continue;
      components.push({ outerRing, holes: getOutlineCavitiesProperty(a.identifier).value.map((h) => h.map((p) => ({ x: p.x, y: p.y, z: p.z }))) });
    }
    return components;
  } catch {
    return []; // property service not ready
  }
}

/** Gating convenience (e.g. "full walls" export, bootstrap candidates). */
export function hasClosedOutline(annotations: readonly OutlineSourceAnnotation[], floorGroupId: string): boolean {
  return resolveOutlineRingsFromAnnotations(annotations, floorGroupId).length > 0;
}

/** Flatten components into the flat ring list (outer rings + holes alike)
 *  the opening wall-face matcher scans (computeOpeningRectangle). */
export function flattenRingComponents(components: readonly OutlineRingComponent[]): { x: number; y: number; z: number }[][] {
  const rings: { x: number; y: number; z: number }[][] = [];
  for (const c of components) {
    rings.push(c.outerRing);
    rings.push(...c.holes);
  }
  return rings;
}

// ─── Construction (rings → graph) ─────────────────────────────────────────

/**
 * Mint an outline subgraph from ring components: one closed node/edge cycle
 * per ring (outer + each hole), tagged `role:'OUTLINE'`, `wallType:'OUTER'`,
 * zero thickness. Used by legacy-outline conversion and outline creation.
 */
export function buildOutlineGraphFromRings(rings: readonly OutlineRingComponent[], floorHeight: number): { nodes: SkeletonNodeData[]; edges: SkeletonEdgeData[] } {
  const nodes: SkeletonNodeData[] = [];
  const edges: SkeletonEdgeData[] = [];

  const addRing = (ring: { x: number; y: number; z?: number }[]): void => {
    const deduped = dedupeClosingPoint(ring);
    if (deduped.length < 3) return;
    const ringNodes: SkeletonNodeData[] = deduped.map((p, i) => ({
      id: uuid(),
      name: `Outline ${nodes.length + i + 1}`,
      position: { x: p.x, y: p.y, z: p.z ?? floorHeight },
      roomIds: [],
      edgeIds: [],
    }));
    for (let i = 0; i < ringNodes.length; i++) {
      const start = ringNodes[i];
      const end = ringNodes[(i + 1) % ringNodes.length];
      const edge: SkeletonEdgeData = {
        id: uuid(),
        name: 'Outline wall',
        startNodeId: start.id,
        endNodeId: end.id,
        roomIds: [],
        leftSize: 0,
        rightSize: 0,
        wallType: 'OUTER',
        role: 'OUTLINE',
      };
      edges.push(edge);
      start.edgeIds.push(edge.id);
      end.edgeIds.push(edge.id);
    }
    nodes.push(...ringNodes);
  };

  for (const component of rings) {
    addRing(component.outerRing);
    for (const hole of component.holes) addRing(hole);
  }
  return { nodes, edges };
}

// ─── Geometry helpers ─────────────────────────────────────────────────────

function signedAreaXY(points: readonly { x: number; y: number }[]): number {
  let area = 0;
  for (let i = 0, j = points.length - 1; i < points.length; j = i++) {
    area += points[j].x * points[i].y - points[i].x * points[j].y;
  }
  return area / 2;
}

/** Even-odd point-in-ring on XY. */
export function pointInRingXY(x: number, y: number, ring: readonly { x: number; y: number }[]): boolean {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const xi = ring[i].x;
    const yi = ring[i].y;
    const xj = ring[j].x;
    const yj = ring[j].y;
    if (yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) inside = !inside;
  }
  return inside;
}

/** Containment probe: midpoint of the first ring segment nudged toward the
 *  ring's interior-ish centroid — robust against shared vertices between
 *  nested rings (a raw vertex could lie exactly ON the other ring). */
function ringMidpointProbe(points: readonly { x: number; y: number }[]): { x: number; y: number } {
  let cx = 0;
  let cy = 0;
  for (const p of points) {
    cx += p.x;
    cy += p.y;
  }
  cx /= points.length;
  cy /= points.length;
  const mx = (points[0].x + points[1].x) / 2;
  const my = (points[0].y + points[1].y) / 2;
  const EPS = 1e-6;
  return { x: mx + (cx - mx) * EPS, y: my + (cy - my) * EPS };
}

/** Ring points in the requested winding (ccw=true → CCW). */
function withWinding(cycle: Cycle, ccw: boolean): { x: number; y: number; z: number }[] {
  const isCcw = cycle.signedArea > 0;
  return isCcw === ccw ? cycle.points : [...cycle.points].reverse();
}

/** Strip an explicit closing point (first == last) if present. */
function dedupeClosingPoint<P extends { x: number; y: number }>(ring: readonly P[]): P[] {
  if (ring.length >= 2) {
    const first = ring[0];
    const last = ring[ring.length - 1];
    if (Math.abs(first.x - last.x) < 1e-9 && Math.abs(first.y - last.y) < 1e-9) return ring.slice(0, -1);
  }
  return [...ring];
}
